import { useMutation } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { Dropdown } from '@/components/Dropdown';
import {
  getBaseRiskStatusOptions,
  riskSeverityOptions,
} from '@/components/legacy-ui/RiskDropdown';
import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { useAxios } from '@/hooks/useAxios';
import { useUploadFile } from '@/hooks/useFiles';
import { useUpdateRisk } from '@/hooks/useRisks';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { Asset, Risk, RiskSeverity, RiskStatus, SeverityDef } from '@/types';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

interface ProposedChangesTabProps {
  risk: Risk;
}

const DELETED_SUBSTATE_LABELS: Record<string, string> = {
  F: 'False Positive',
  S: 'Out of Scope',
  D: 'Duplicate',
  O: 'Other',
};

/**
 * Returns a human-readable label for the full proposed status string.
 * For deleted statuses with substates (e.g. "DCF"), appends the substate label.
 * Examples:
 *   "OC" -> "Open" (legacy) / "Demonstrated" (new)
 *   "DCF" -> "Rejected - False Positive"
 *   "DHS" -> "Rejected - Out of Scope"
 */
function getProposedStatusLabel(
  proposedStatus: string,
  mode: 'legacy' | 'new'
): string {
  if (!proposedStatus) return '';

  const firstChar = proposedStatus[0];
  const baseLabel = getStatusLabel(firstChar as RiskStatus, mode);

  // For deleted statuses with a 3-char substate code, append substate label
  if (firstChar === RiskStatus.Deleted && proposedStatus.length >= 3) {
    const substateChar = proposedStatus[2];
    const substateLabel = DELETED_SUBSTATE_LABELS[substateChar];
    if (substateLabel) {
      return `${baseLabel} - ${substateLabel}`;
    }
  }

  return baseLabel;
}

interface SaveProposalData {
  key: string;
  proposedStatus: string;
  proposedComment: string;
  proposedEvidence: string;
  proposedBy: string;
  proposedAt: string;
  proposedConversationID?: string;
}

// Fetch file content by resolving the presigned URL from /file endpoint
async function fetchFileContent(
  axios: AxiosInstance,
  name: string,
  params?: Record<string, string>
): Promise<string | null> {
  try {
    const { data } = await axios.get('/file', {
      params: { name, ...params },
    });
    const url = data?.url;
    if (!url) return null;
    // Follow the presigned URL to get actual file content
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.text();
  } catch {
    return null;
  }
}

async function fetchEvidenceFiles(
  axios: AxiosInstance,
  riskKey: string
): Promise<Array<{
  attachmentPath: string;
  content: string;
  asset?: Asset;
}> | null> {
  try {
    const { data } = await axios.post(
      '/my',
      {
        node: {
          relationships: [
            {
              label: 'HAS_VULNERABILITY',
              target: {
                labels: ['Risk'],
                filters: [{ field: 'key', operator: '=', value: riskKey }],
              },
            },
          ],
        },
      },
      { params: { tree: true } }
    );

    const seen = new Set<string>();
    const entries: Array<{ attachmentPath: string; asset?: Asset }> = [];

    for (const entry of data) {
      const attachmentPath =
        entry?.node?.relationships?.[0]?.result?.hasvulnerabilities?.[0]
          ?.attachmentPath;
      if (!attachmentPath || seen.has(attachmentPath)) continue;
      seen.add(attachmentPath);
      const asset =
        entry?.node?.result?.assets?.[0] ||
        entry?.node?.result?.repositories?.[0] ||
        entry?.node?.result?.awsresources?.[0] ||
        entry?.node?.result?.azureresources?.[0] ||
        entry?.node?.result?.gcpresources?.[0];
      entries.push({ attachmentPath, asset });
    }

    // Fetch actual content for each unique proof file
    const results = await Promise.all(
      entries.map(async ({ attachmentPath, asset }) => {
        const content = await fetchFileContent(axios, attachmentPath);
        return { attachmentPath, content: content || '', asset };
      })
    );

    return results;
  } catch {
    return null;
  }
}

async function fetchAffectedAssets(
  axios: AxiosInstance,
  riskKey: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Array<{ key: string; name: string; [k: string]: any }> | null> {
  try {
    const { data } = await axios.post('/my', {
      node: {
        labels: ['Asset'],
        filters: [{ field: 'status', operator: '=', value: 'D', not: true }],
        relationships: [
          {
            label: ['HAS_VULNERABILITY', 'HAS_PORT', 'HAS_WEBPAGE'],
            length: 3,
            target: {
              labels: ['Risk'],
              filters: [{ field: 'key', operator: '=', value: riskKey }],
            },
          },
        ],
      },
    });

    // Graph query returns { assets: [...], count: N } or a flat array

    const assets = Array.isArray(data) ? data : data?.assets || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return assets.map((entry: any) => ({
      key: entry.key || '',
      name: entry.name || entry.identifier || entry.key || '',
      ...entry,
    }));
  } catch {
    return null;
  }
}

async function fetchConversations(
  axios: AxiosInstance,
  conversationId?: string
): Promise<{
  parent?: { id: string; messages: any[] }; // eslint-disable-line @typescript-eslint/no-explicit-any
  children?: Array<{ id: string; messages: any[] }>; // eslint-disable-line @typescript-eslint/no-explicit-any
} | null> {
  if (!conversationId) return null;
  try {
    const { data: parentMessages } = await axios.get('/my', {
      params: { key: `#message#${conversationId}`, user: 'true' },
    });

    const { data: childConvos } = await axios.get('/my', {
      params: {
        label: 'conversation',
        key: `parent_id:${conversationId}`,
        user: 'true',
      },
    });

    const childList = Array.isArray(childConvos)
      ? childConvos
      : childConvos?.conversations || [];
    const children = await Promise.all(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      childList.map(async (conv: any) => {
        const id = conv.uuid || conv.id;
        if (!id) return null;
        try {
          const { data: msgs } = await axios.get('/my', {
            params: { key: `#message#${id}`, user: 'true' },
          });
          return { id, messages: msgs || [] };
        } catch {
          return { id, messages: [] };
        }
      })
    );

    return {
      parent: { id: conversationId, messages: parentMessages || [] },
      children: children.filter(Boolean),
    };
  } catch {
    return null;
  }
}

export function ProposedChangesTab({ risk }: ProposedChangesTabProps) {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { isPraetorianUser, me } = useAuth();
  const { mode } = useVulnerabilityStatusMode();
  const { mutateAsync: updateRisk } = useUpdateRisk();
  const { mutateAsync: updateFile } = useUploadFile();
  const axios = useAxios();

  const [comment, setComment] = useState(risk.proposedComment || '');
  const [evidence, setEvidence] = useState(risk.proposedEvidence || '');
  const [proposedStatus, setProposedStatus] = useState(
    risk.proposedStatus || ''
  );
  const [proposedBy, setProposedBy] = useState(risk.proposedBy || '');
  const [proposedAt, setProposedAt] = useState(risk.proposedAt || '');

  const [isConfirmingAccept, setIsConfirmingAccept] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState(comment);

  const [isEditingEvidence, setIsEditingEvidence] = useState(false);
  const [evidenceDraft, setEvidenceDraft] = useState(evidence);

  const saveProposalMutation = useMutation({
    mutationFn: async (proposalData: SaveProposalData) => {
      const response = await axios.put('/risk', proposalData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: q => {
          const key = q.queryKey as string[];
          return key[0] === 'MY' && key[1] === 'risk';
        },
      });
    },
  });

  if (!risk.proposedStatus) return null;

  const currentStatusChar = proposedStatus[0] || '';
  const currentSeverityChar =
    proposedStatus.length >= 2 ? proposedStatus[1] : '';

  const statusOptions = getBaseRiskStatusOptions(isPraetorianUser, mode);
  const currentSeverityOption = riskSeverityOptions.find(
    o => o.value === currentSeverityChar
  );
  const currentSeverityLabel =
    currentSeverityOption?.label ||
    SeverityDef[currentSeverityChar as RiskSeverity] ||
    currentSeverityChar;

  const statusPlaceholder =
    getProposedStatusLabel(proposedStatus, mode) || currentStatusChar;

  const handleStatusChange = (value: string) => {
    setProposedStatus(`${value}${currentSeverityChar}`);
  };

  const handleSeverityChange = (value: string) => {
    setProposedStatus(`${currentStatusChar}${value}`);
  };

  const statusSeverityDirty = proposedStatus !== (risk.proposedStatus || '');

  const handleSaveStatusSeverity = () => {
    const newProposedBy = me || '';
    const newProposedAt = new Date().toISOString();
    setProposedBy(newProposedBy);
    setProposedAt(newProposedAt);
    saveProposalMutation.mutate({
      key: risk.key,
      proposedStatus,
      proposedComment: comment,
      proposedEvidence: evidence,
      proposedBy: newProposedBy,
      proposedAt: newProposedAt,
      proposedConversationID: risk.proposedConversationID,
    });
  };

  const handleAccept = async () => {
    try {
      await updateRisk({
        key: risk.key,
        name: risk.name,
        status: proposedStatus,
        comment: comment,
        showSnackbar: false,
      });

      if (evidence.trim()) {
        await updateFile({
          name: `evidence/${risk.key}`,
          content: evidence,
          ignoreSnackbar: true,
        });
      }

      queryClient.invalidateQueries();
      toast.success('Proposal accepted — risk updated');
    } catch {
      toast.error('Failed to accept proposal');
    }
  };

  const handleReject = () => {
    if (risk.proposedConversationID) {
      navigate(
        `/${userId}/${AuthenticatedRoute.INSIGHTS}?conversationId=${risk.proposedConversationID}`
      );
    }
  };

  const handleSubmitFeedback = async () => {
    setIsSubmittingFeedback(true);
    try {
      // Fetch all data in parallel — partial failures are OK
      const [
        definitionResult,
        evidenceResult,
        evidenceFilesResult,
        conversationResult,
        affectedAssetsResult,
      ] = await Promise.allSettled([
        // Definition file (instance-level, with account and global fallbacks)
        fetchFileContent(axios, `definitions/${risk.key}`).then(
          async content => {
            if (content) return content;
            // Fallback to account-level definition
            const accountDef = await fetchFileContent(
              axios,
              `definitions/${risk.name}`
            );
            if (accountDef) return accountDef;
            // Fallback to global definition
            return fetchFileContent(axios, `definitions/${risk.name}`, {
              global: 'true',
            });
          }
        ),
        // Evidence file
        fetchFileContent(axios, `evidence/${risk.key}`),
        // Proof files via evidence files graph query
        fetchEvidenceFiles(axios, risk.key),
        // Conversations (parent + children)
        fetchConversations(axios, risk.proposedConversationID),
        // Affected assets
        fetchAffectedAssets(axios, risk.key),
      ]);

      const feedbackData = {
        submittedBy: me,
        submittedAt: new Date().toISOString(),
        comment: feedbackComment,
        risk,
        ...(definitionResult.status === 'fulfilled' && definitionResult.value
          ? { definition: definitionResult.value }
          : {}),
        ...(evidenceResult.status === 'fulfilled' && evidenceResult.value
          ? { evidenceFile: evidenceResult.value }
          : {}),
        ...(evidenceFilesResult.status === 'fulfilled' &&
        evidenceFilesResult.value
          ? { proofFiles: evidenceFilesResult.value }
          : {}),
        ...(conversationResult.status === 'fulfilled' &&
        conversationResult.value
          ? { conversations: conversationResult.value }
          : {}),
        ...(affectedAssetsResult.status === 'fulfilled' &&
        affectedAssetsResult.value
          ? { affectedAssets: affectedAssetsResult.value }
          : {}),
      };

      await updateFile({
        name: `${me}-triage-feedback-${new Date().toISOString().replace(/[:.]/g, '-')}.json`,
        content: JSON.stringify(feedbackData, null, 2),
        praetorian: true,
        ignoreSnackbar: true,
      });

      toast.success('Feedback submitted successfully');
      setIsFeedbackModalOpen(false);
      setFeedbackComment('');
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className="space-y-6">
      {(proposedBy || proposedAt) && (
        <div className="space-y-1">
          <h3 className="text-default text-sm font-semibold">Proposed By</h3>
          <p className="text-default-light text-sm">
            {proposedBy || 'Unknown agent'}
            {proposedAt && (
              <span className="text-default-light ml-2">
                {new Date(proposedAt).toLocaleString()}
              </span>
            )}
          </p>
        </div>
      )}

      <div className="flex items-center gap-4">
        <div className="space-y-1">
          <h3 className="text-default text-sm font-semibold">Status</h3>
          <Dropdown
            placeholder={statusPlaceholder}
            styleType="rounded"
            size="sm"
            menu={{
              items: statusOptions.map(o => ({
                label: o.label,
                value: String(o.value),
                icon: o.icon,
                onClick: () => handleStatusChange(String(o.value)),
              })),
              placement: 'bottom-start',
            }}
          />
        </div>
        <div className="space-y-1">
          <h3 className="text-default text-sm font-semibold">Severity</h3>
          <Dropdown
            placeholder={currentSeverityLabel}
            styleType="rounded"
            size="sm"
            menu={{
              items: riskSeverityOptions.map(o => ({
                label: o.label,
                value: String(o.value),
                icon: o.icon,
                onClick: () => handleSeverityChange(String(o.value)),
              })),
              placement: 'bottom-start',
            }}
          />
        </div>
        {statusSeverityDirty && (
          <Button
            onClick={handleSaveStatusSeverity}
            variant="default"
            size="sm"
          >
            Save
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-default text-sm font-semibold">Comment</h3>
          <button
            type="button"
            className="text-brand text-xs font-medium hover:underline"
            onClick={() => {
              setCommentDraft(comment);
              setIsEditingComment(true);
            }}
          >
            Edit
          </button>
        </div>
        <div className="bg-chariot-layer1 rounded-lg p-4">
          {comment ? (
            <MarkdownPreview source={comment} />
          ) : (
            <p className="text-default-light text-sm italic">
              No comment provided
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-default text-sm font-semibold">
            Drafted Evidence
          </h3>
          <button
            type="button"
            className="text-brand text-xs font-medium hover:underline"
            onClick={() => {
              setEvidenceDraft(evidence);
              setIsEditingEvidence(true);
            }}
          >
            Edit
          </button>
        </div>
        <div className="bg-chariot-layer1 rounded-lg p-4">
          {evidence ? (
            <MarkdownPreview source={evidence} />
          ) : (
            <p className="text-default-light text-sm italic">
              No evidence provided
            </p>
          )}
        </div>
      </div>

      <div className="border-chariot-stroke flex items-center gap-3 border-t pt-4">
        <Button onClick={() => setIsConfirmingAccept(true)} variant="default">
          Accept Proposal
        </Button>
        <Button
          onClick={handleReject}
          variant="secondary"
          disabled={!risk.proposedConversationID}
        >
          Resume Conversation
        </Button>
        {isPraetorianUser && (
          <Button
            onClick={() => setIsFeedbackModalOpen(true)}
            variant="outline"
          >
            Submit Feedback
          </Button>
        )}
      </div>

      <Modal
        open={isConfirmingAccept}
        onClose={() => setIsConfirmingAccept(false)}
        title="Accept Proposal"
        footer={{
          text: 'Confirm',
          onClick: async () => {
            setIsConfirmingAccept(false);
            await handleAccept();
          },
        }}
      >
        <p className="text-default-light text-sm">
          Are you sure you&apos;d like to accept the proposal? This will update
          the risk&apos;s status, add a new note, and overwrite any existing
          evidence content.
        </p>
      </Modal>

      <Modal
        size="6xl"
        open={isEditingComment}
        onClose={() => setIsEditingComment(false)}
        title="Edit Comment"
        footer={{
          text: 'Save',
          isLoading: false,
          onClick: async () => {
            const newProposedBy = me || '';
            const newProposedAt = new Date().toISOString();
            setComment(commentDraft);
            setProposedBy(newProposedBy);
            setProposedAt(newProposedAt);
            setIsEditingComment(false);
            await saveProposalMutation.mutateAsync({
              key: risk.key,
              proposedStatus,
              proposedComment: commentDraft,
              proposedEvidence: evidence,
              proposedBy: newProposedBy,
              proposedAt: newProposedAt,
              proposedConversationID: risk.proposedConversationID,
            });
          },
        }}
      >
        <div className="h-[60vh]">
          <MarkdownEditor
            value={commentDraft}
            onChange={value => setCommentDraft(value || '')}
            filePathPrefix="definitions/files"
          />
        </div>
      </Modal>

      <Modal
        size="6xl"
        open={isEditingEvidence}
        onClose={() => setIsEditingEvidence(false)}
        title="Edit Drafted Evidence"
        footer={{
          text: 'Save',
          isLoading: false,
          onClick: async () => {
            const newProposedBy = me || '';
            const newProposedAt = new Date().toISOString();
            setEvidence(evidenceDraft);
            setProposedBy(newProposedBy);
            setProposedAt(newProposedAt);
            setIsEditingEvidence(false);
            await saveProposalMutation.mutateAsync({
              key: risk.key,
              proposedStatus,
              proposedComment: comment,
              proposedEvidence: evidenceDraft,
              proposedBy: newProposedBy,
              proposedAt: newProposedAt,
              proposedConversationID: risk.proposedConversationID,
            });
          },
        }}
      >
        <div className="h-[60vh]">
          <MarkdownEditor
            value={evidenceDraft}
            onChange={value => setEvidenceDraft(value || '')}
            filePathPrefix="definitions/files"
          />
        </div>
      </Modal>

      <Modal
        open={isFeedbackModalOpen}
        onClose={() => {
          setIsFeedbackModalOpen(false);
          setFeedbackComment('');
        }}
        title="Submit Triage Feedback"
        footer={{
          text: 'Submit',
          isLoading: isSubmittingFeedback,
          disabled: isSubmittingFeedback,
          onClick: handleSubmitFeedback,
        }}
      >
        <div className="space-y-4">
          <p className="text-default-light text-sm">
            Provide feedback on the triage proposal&apos;s accuracy.
          </p>
          <div className="space-y-1">
            <label
              htmlFor="feedback-comment"
              className="text-default text-sm font-medium"
            >
              Comment (optional)
            </label>
            <textarea
              id="feedback-comment"
              value={feedbackComment}
              onChange={e => setFeedbackComment(e.target.value)}
              placeholder="Add any additional context or notes..."
              rows={5}
              className="border-chariot-stroke bg-chariot-layer1 text-default placeholder:text-default-light focus:ring-brand w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
